import {
  type IPackagesSections,
  PackagesSections,
} from '@bridebook/toolbox/src/map-packages-to-sections';
import {
  EPricingDealType,
  type PricingItem,
} from '@bridebook/ui/src/components/pricing-accordion/types';
import { i18n } from 'i18next';
import { getI18n } from '@bridebook/web/lib/i18n/getI18n';
import { IncludedInPricingLabels } from '@bridebook/web/app-shared/lib/supplier/data/included-in-pricing-labels';
import { Trans } from 'next-i18next';
import { getPackagesPrice } from '@bridebook/web/app-shared/lib/supplier/utils';

type TVenuePricingLabels = {
  [key in EPricingDealType]: {
    label?: string;
    labelInfo?: string;
  };
};

const addInitiallyExpandedFlag = (items: PricingItem[]): PricingItem[] => {
  if (items.length === 0) return [];

  // Find the dry hire item
  const venueHireItem = items.find((item) => item.dealType === EPricingDealType.VENUE_HIRE);

  if (venueHireItem) {
    return items.map((item) => ({
      ...item,
      initiallyExpanded: item === venueHireItem,
    }));
  }

  // Find the item with the lowest price
  const cheapestItem = items.reduce(
    (cheapest, item) => (item.minPrice < cheapest.minPrice ? item : cheapest),
    items[0],
  );

  return items.map((item) => ({
    ...item,
    initiallyExpanded: item === cheapestItem,
  }));
};

const getVenuePricingLabels = (i18n: i18n): TVenuePricingLabels => ({
  [EPricingDealType.VENUE_HIRE]: {
    label: i18n.t('supplier:venue.pricing.hire.title.v2'),
  },
  [EPricingDealType.CATERING]: {
    label: i18n.t('supplier:venue.pricing.catering.title.v2'),
  },
  [EPricingDealType.PACKAGES_PP]: {
    label: i18n.t('supplier:venue.pricing.packages.title.spp.v2'),
  },
  [EPricingDealType.PACKAGES_ALL]: {
    label: i18n.t('supplier:venue.pricing.packages.title.all.v2'),
  },
});
type LabelKey = keyof typeof IncludedInPricingLabels;
const parseIncludedInPricing = (included: Record<string, boolean>): string[] =>
  Object.entries(included)
    .filter(([_, value]) => Boolean(value))
    .map(([key, _]) => IncludedInPricingLabels[key as LabelKey]?.());

const reduceFn = (acc: Record<string, any>, val: string) => {
  acc[val] = true;
  return acc;
};

export const getVenuePricingItems = ({ packages }: { packages: IPackagesSections }) => {
  const i18n = getI18n();
  const labels = getVenuePricingLabels(i18n);

  const getPrice = (type: keyof typeof PackagesSections, index: 0 | 1) =>
    parseFloat(String(getPackagesPrice(type, index, packages) || 0));

  const [venueHireOnlyMinPrice, venueHireOnlyMaxPrice] = [
    getPrice('venueHire', 0),
    getPrice('venueHire', 1),
  ];

  const [packagesMinPricePh, packagesMaxPricePh] = [
    getPrice('pricePerHead', 0),
    getPrice('pricePerHead', 1),
  ];

  const [packagesTotalCostMinPrice, packagesTotalCostMaxPrice] = [
    getPrice('totalInclusive', 0),
    getPrice('totalInclusive', 1),
  ];

  const [inHouseCateringMinPricePh, inHouseCateringMaxPricePh] = [
    getPrice('inHouseCatering', 0),
    getPrice('inHouseCatering', 1),
  ];

  let packagesTotalCostNoOfGuests = packages?.totalInclusive?.guests;
  let packagesMinNoOfGuests = (packages?.pricePerHead?.guests as number[])?.[0];
  const venueHireSection = (packages?.venueHire?.included || []).reduce(reduceFn, {});
  const cateringSection = (packages?.inHouseCatering?.included || []).reduce(reduceFn, {});
  const pricePerHeadSection = (packages?.pricePerHead?.included || []).reduce(reduceFn, {});
  const totalInclusivePriceSection = (packages?.totalInclusive?.included || []).reduce(
    reduceFn,
    {},
  );

  const venueHireOnlyInfo = packages?.venueHire?.description;
  const inHouseCateringInfo = packages?.inHouseCatering?.description;
  const packagesInfo = packages?.pricePerHead?.description;
  const packagesTotalCostInfo = packages?.totalInclusive?.description;

  packagesMinNoOfGuests = parseInt(String(packagesMinNoOfGuests || 0), 10);
  packagesTotalCostNoOfGuests = parseInt(String(packagesTotalCostNoOfGuests || 0), 10);

  const venueHire = venueHireOnlyMinPrice > 0 || venueHireOnlyMaxPrice > 0;
  const packagesSection = packagesMinPricePh > 0 || packagesMaxPricePh > 0;
  const inHouseCatering = inHouseCateringMinPricePh > 0 || inHouseCateringMaxPricePh > 0;
  const packagesTotalSection = packagesTotalCostMinPrice > 0 || packagesTotalCostMaxPrice > 0;

  const items: PricingItem[] = [
    {
      dealType: EPricingDealType.VENUE_HIRE,
      label: labels[EPricingDealType.VENUE_HIRE].label || '',
      minPrice: venueHireOnlyMinPrice,
      maxPrice: venueHireOnlyMaxPrice,
      info: venueHireOnlyInfo,
      visible: venueHire,
      includedInPricing: parseIncludedInPricing(venueHireSection),
      initiallyExpanded: false,
    },
    {
      dealType: EPricingDealType.CATERING,
      label: labels[EPricingDealType.CATERING].label || '',
      labelInfo: labels[EPricingDealType.CATERING].labelInfo,
      minPrice: inHouseCateringMinPricePh,
      maxPrice: inHouseCateringMaxPricePh,
      info: inHouseCateringInfo,
      visible: inHouseCatering,
      includedInPricing: parseIncludedInPricing(cateringSection),
      initiallyExpanded: false,
    },
    {
      dealType: EPricingDealType.PACKAGES_PP,
      label: labels[EPricingDealType.PACKAGES_PP].label || '',
      labelInfo: labels[EPricingDealType.PACKAGES_PP].labelInfo,
      minPrice: packagesMinPricePh,
      maxPrice: packagesMaxPricePh,
      guests: packagesMinNoOfGuests,
      guestsLabel: i18n.t('supplier:venue.pricing.packages.label'),
      info: packagesInfo,
      visible: packagesSection,
      includedInPricing: parseIncludedInPricing(pricePerHeadSection),
      guestsInfo: (
        <Trans ns="supplier" i18nKey="supplier.venue.pricing.minimumNumberOfGuests">
          Minimum number of guests: <span className="guest-count">{{ packagesMinNoOfGuests }}</span>
        </Trans>
      ),
      initiallyExpanded: false,
    },
    {
      dealType: EPricingDealType.PACKAGES_ALL,
      label: labels[EPricingDealType.PACKAGES_ALL].label || '',
      labelInfo: labels[EPricingDealType.PACKAGES_ALL].labelInfo,
      minPrice: packagesTotalCostMinPrice,
      maxPrice: packagesTotalCostMaxPrice,
      guests: packagesTotalCostNoOfGuests,
      guestsLabel: i18n.t('supplier:venue.pricing.packages.label.all'),
      info: packagesTotalCostInfo,
      visible: packagesTotalSection,
      includedInPricing: parseIncludedInPricing(totalInclusivePriceSection),
      guestsInfo: (
        <Trans ns="supplier" i18nKey="supplier.venue.pricing.numberOfGuestsIncluded">
          Number of guests included:{' '}
          <span className="guest-count">{{ packagesTotalCostNoOfGuests }}</span>
        </Trans>
      ),
      initiallyExpanded: false,
    },
  ];

  return addInitiallyExpandedFlag(items.filter((item) => item.visible));
};
